import { Heading, Stack } from "@chakra-ui/react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { resetPassword } from "../api/auth";
import { Button } from "../components/button";
import { Field } from "../components/field";
import LoginShell from "../components/login-shell";
import { PasswordInput } from "../components/password-input";
import { toaster } from "../components/toaster";

interface ResetPasswordForm {
  password: string;
}

export default function ResetPasswordPage() {
  const { uid, token } = useParams<{ uid: string; token: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = React.useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordForm>();
  const onSubmit = handleSubmit(async ({ password }) => {
    setIsLoading(true);
    try {
      await resetPassword(uid!, token!, password);
      toaster.create({
        type: "success",
        title: "Password Reset",
        description: "Your password has been reset. You can now sign in.",
      });
      navigate("/login");
    } catch {
      toaster.create({
        type: "error",
        title: "Error",
        description: "This password reset link has expired.",
      });
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <LoginShell>
      <Stack color="fg.accent" gap={{ base: "2", md: "3" }} textAlign="center">
        <Heading size={{ base: "2xl", md: "3xl" }}>New Password</Heading>
      </Stack>
      <form onSubmit={onSubmit}>
        <Stack gap="6">
          <Field
            label="Password"
            invalid={!!errors.password}
            errorText={errors.password?.message}
          >
            <PasswordInput
              {...register("password", {
                required: "Please provide a password.",
                minLength: {
                  value: 8,
                  message: "Password must be at least 8 characters.",
                },
              })}
            />
          </Field>
          <Stack gap="4">
            <Button loading={isLoading} type="submit">
              Reset Password
            </Button>
          </Stack>
        </Stack>
      </form>
    </LoginShell>
  );
}
